import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';

import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { handleError } from '../../context/errors/errorUtils';
import { useModal } from '../../context/ModalContext';
import Modal from '../UpModal/UpModal';
import styles from './Logo.module.css';

import avatarDefault from '../../assets/images/avatars/base/default.png';
import avatarMan1 from '../../assets/images/avatars/base/man1.svg';
import avatarMan2 from '../../assets/images/avatars/base/man2.svg';
import avatarMan3 from '../../assets/images/avatars/base/man3.svg';
import avatarWoman1 from '../../assets/images/avatars/base/woman1.svg';
import avatarWoman2 from '../../assets/images/avatars/base/woman2.svg';
import avatarWoman3 from '../../assets/images/avatars/base/woman3.svg';
import avatarWoman4 from '../../assets/images/avatars/base/woman4.svg';
import avatarWoman5 from '../../assets/images/avatars/base/woman5.svg';
import imgVideo from '../../assets/images/icons/video.svg';  

const avatarMap = {
  default: avatarDefault,
  man1: avatarMan1,
  man2: avatarMan2,
  man3: avatarMan3,
  woman1: avatarWoman1,
  woman2: avatarWoman2,
  woman3: avatarWoman3,
  woman4: avatarWoman4,
  woman5: avatarWoman5,
};

const PDF_LEVEL_ORDER = ['A1', 'A2.1', 'A2.2', 'B1.1', 'B1.2', 'B1.3', 'B2'];
const FREE_PDF_LIMIT = 3;

const UI_TEXT_RU = {
  tabVideos: 'Видео',
  tabPdf: 'PDF',
  tabProfile: 'Профиль',
  videoNotFound: 'Видео не найдено',
  filesNotFound: 'Файлы не найдены.',
  pdfTitle: 'PDF-материалы по уровням',
  pdfSubtitle: 'Темы видно всем, полный доступ к материалам открывается в All Inclusive.',
  pdfFullPack: 'Полный пакет в All Inclusive',
  pdfChooseFile: 'Выберите файл ниже',
  download: 'Скачать',
  profileOpen: 'Открыть профиль',
  profileLanguageLevel: 'Языки и уровень',
  phrasesInPractice: 'фраз в практике',
};

const buildVideoPromoText = (count) =>
  `${count} коротких видео с научными фактами о том, как мозг учит язык. ` +
  'Посмотрите их за 15 минут и избегайте ошибок, которые обычно растягивают обучение ' +
  'на годы и приводят к лишним тратам на курсы.';

const isLogoDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    const fromQuery = window.location.search.includes('logoDebug=1');
    const fromStorage = window.localStorage.getItem('logoDebug') === '1';
    return fromQuery || fromStorage || !!import.meta.env.DEV;
  } catch {
    return !!import.meta.env.DEV;
  }
};

const logoDebug = (...args) => {
  if (!isLogoDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log('[Logo]', ...args);
};

const getAvatarSrc = (avatar, isNative, site) => {
  if (!avatar) return avatarMap.default;
  if (avatarMap[avatar]) return avatarMap[avatar];
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/')) return isNative ? site + avatar : avatar;
  return avatarMap.default;
};

const isDirectVideoFile = (url = '') => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);

const getPdfLevelFromFile = (fileName = '') => {
  const match = String(fileName).match(/^(A1|A2\.1|A2\.2|B1\.1|B1\.2|B1\.3|B2)_/);
  return match?.[1] || null;
};

const buildPdfGroupsFromMap = (topicsMap = {}) => {
  const grouped = PDF_LEVEL_ORDER.reduce((acc, level) => {
    acc[level] = [];
    return acc;
  }, {});

  Object.entries(topicsMap).forEach(([topic, file]) => {
    const level = getPdfLevelFromFile(file);
    if (!level || !grouped[level]) return;
    grouped[level].push({ topic, file });
  });

  return PDF_LEVEL_ORDER
    .map((level) => ({
      level,
      items: grouped[level].sort((a, b) => a.file.localeCompare(b.file)),
    }))
    .filter((group) => group.items.length > 0);
};

const pickFirstFreePdfFiles = (groups, limit = FREE_PDF_LIMIT) => {
  const orderedFiles = groups.flatMap((group) => group.items.map((item) => item.file));
  return new Set(orderedFiles.slice(0, limit));
};

const InlineVideo = ({ url, title, showTitle = true }) => {
  if (!url) return null;

  if (isDirectVideoFile(url)) {
    return (
      <div className={styles.inlineVideo}>
        {showTitle && title && <h3 className={styles.inlineVideoTitle}>{title}</h3>}
        <video className={styles.inlineVideoPlayer} controls playsInline preload="metadata" src={url} />
      </div>
    );
  }

  return (
    <div className={styles.inlineVideo}>
      {showTitle && title && <h3 className={styles.inlineVideoTitle}>{title}</h3>}
      <div className={styles.inlineVideoFrameWrap}>
        <iframe
          className={styles.inlineVideoFrame}
          src={url}
          title={title || 'video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

const Logo = ({
  userId,
  phrasesCount,
  forceLegacyPdf = false,
  onGoToVip,
  onOpenProfile,
  onOpenLanguageSettings,
}) => {
  const { t, baseLanguage, targetLanguage, brandName, site } = useAppConfig();
  //const site = 'http://localhost:5001';
  const { userInfo } = useAuth();
  const { openModal, closeModal } = useModal();

  const isNative = Capacitor.isNativePlatform();
  const isCyrillicLang = baseLanguage === 'ru' || baseLanguage === 'uk';
  const vipLevel = userInfo?.vip || 0;
  const isLocked = ![6, 7].includes(vipLevel);

  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(isCyrillicLang ? 'videos' : 'profile');

  const [linksList, setLinksList] = useState([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);

  const [legacyPdfList, setLegacyPdfList] = useState([]);
  const [legacyPdfPath, setLegacyPdfPath] = useState('');
  const [useLegacyPdfLayout, setUseLegacyPdfLayout] = useState(false);

  const [newPdfGroups, setNewPdfGroups] = useState([]);
  const [newPdfPath, setNewPdfPath] = useState('');
  const [expandedPdfLevels, setExpandedPdfLevels] = useState({});
  const [freePdfFiles, setFreePdfFiles] = useState(new Set());

  const modalTabs = useMemo(() => {
    if (isCyrillicLang) {
      return [
        { key: 'videos', label: UI_TEXT_RU.tabVideos },
        { key: 'pdf', label: UI_TEXT_RU.tabPdf },
        { key: 'profile', label: t.settings.profileTab || UI_TEXT_RU.tabProfile },
      ];
    }
    return [{ key: 'profile', label: t.settings.profileTab || UI_TEXT_RU.tabProfile }];
  }, [isCyrillicLang, t.settings?.profileTab]);

  useEffect(() => {
    setActiveTab(isCyrillicLang ? 'videos' : 'profile');
  }, [isCyrillicLang]);

  // Track modal open/close with ModalContext to prevent hints
  useEffect(() => {
    if (showModal) {
      //console.log('[Logo] Modal opened');
      openModal('logo');
    } else {
      //console.log('[Logo] Modal closed');
      closeModal('logo');
    }
  }, [showModal, openModal, closeModal]);

  useEffect(() => {
    logoDebug('video effect start', { isCyrillicLang, baseLanguage, site, userId });

    if (!isCyrillicLang) {
      logoDebug('video fetch skipped: baseLanguage is not ru/uk');
      return;
    }

    const fetchLinks = async () => {
      const candidatePaths = [
        `${site}/backend/assets/video/sci/links_list.json`,
        `${site}/backend/assets/video/links_list.json`,
      ];
      const attempts = [];

      for (const path of candidatePaths) {
        try {
          logoDebug('video fetch attempt', path);
          const response = await fetch(path);
          attempts.push({ path, status: response.status, ok: response.ok });
          logoDebug('video fetch response', { path, status: response.status, ok: response.ok });
          if (!response.ok) continue;

          const data = await response.json();
          logoDebug('video list loaded', { path, items: Array.isArray(data) ? data.length : 0 });
          setLinksList(Array.isArray(data) ? data : []);
          return;
        } catch (error) {
          attempts.push({ path, error: String(error?.message || error) });
          logoDebug('video fetch error', { path, error });
        }
      }

      handleError({
        code: 'ERROR_FETCH_LOGO_LINKS',
        error: new Error('Unable to fetch video links list'),
        context: { module: 'Logo', userId, attempts, site },
        showToUser: false,
      });
      logoDebug('video list failed', attempts);
      setLinksList([]);
    };

    fetchLinks();
  }, [baseLanguage, isCyrillicLang, site, userId]);

  const videoLinks = useMemo(
    () => linksList.filter((item) => item?.type === 'video' && item?.url),
    [linksList]
  );

  useEffect(() => {
    logoDebug('video links ready', {
      linksList: linksList.length,
      videoLinks: videoLinks.length,
      activeTab,
    });
  }, [activeTab, linksList.length, videoLinks.length]);

  useEffect(() => {
    setActiveVideoIndex(null);
  }, [videoLinks.length]);

  useEffect(() => {
    if (activeTab !== 'pdf' || !isCyrillicLang) return;

    const fetchLegacyPdfList = async () => {
      const langFolder = targetLanguage + baseLanguage;
      logoDebug('pdf legacy fetch start', { langFolder, targetLanguage, baseLanguage, site });
      const response = await fetch(`${site}/backend/assets/pdf/${langFolder}/pdf_list_${targetLanguage}.json`);
      if (!response.ok) throw new Error(`Legacy PDF list request failed: ${response.status}`);

      const data = await response.json();
      setLegacyPdfPath(
        isNative ? `${site}/backend/assets/pdf/${langFolder}/` : `/backend/assets/pdf/${langFolder}/`
      );
      setLegacyPdfList(Array.isArray(data) ? data : []);
      setUseLegacyPdfLayout(true);
      setNewPdfGroups([]);
      setExpandedPdfLevels({});
      setFreePdfFiles(new Set());
    };

    const fetchNewPdfCatalog = async () => {
      if (!['de', 'en'].includes(targetLanguage)) {
        throw new Error(`Unsupported target language for dialogs catalog: ${targetLanguage}`);
      }

      logoDebug('pdf modern fetch start', { targetLanguage, site });
      const response = await fetch(`${site}/backend/assets/pdf/dialogs/${targetLanguage}/topics_map.json`);
      if (!response.ok) throw new Error(`Dialog topics map request failed: ${response.status}`);

      const topicsMap = await response.json();
      const groups = buildPdfGroupsFromMap(topicsMap);
      const freeFiles = pickFirstFreePdfFiles(groups);

      setNewPdfPath(
        isNative
          ? `${site}/backend/assets/pdf/dialogs/${targetLanguage}/`
          : `/backend/assets/pdf/dialogs/${targetLanguage}/`
      );
      setNewPdfGroups(groups);
      setFreePdfFiles(freeFiles);
      setUseLegacyPdfLayout(false);
      setLegacyPdfList([]);
      setExpandedPdfLevels(groups[0] ? { [groups[0].level]: true } : {});
    };

    const fetchPdfData = async () => {
      const forceLegacyLayout =
        forceLegacyPdf === true ||
        userInfo?.legacyPathHome === true ||
        userInfo?.pathHomeVersion === 'old' ||
        userInfo?.path_home_version === 'old';

      if (forceLegacyLayout) {
        logoDebug('pdf: force legacy layout by userInfo flag');
        await fetchLegacyPdfList();
        return;
      }

      try {
        await fetchNewPdfCatalog();
      } catch (newLayoutError) {
        logoDebug('pdf modern fetch failed, fallback to legacy', newLayoutError);
        try {
          await fetchLegacyPdfList();
        } catch (legacyLayoutError) {
          logoDebug('pdf legacy fallback failed', legacyLayoutError);
          handleError({
            code: 'ERROR_FETCH_LOGO_PDF',
            error: legacyLayoutError,
            context: { module: 'Logo', userId, fallbackError: String(newLayoutError) },
            showToUser: false,
          });
          setUseLegacyPdfLayout(true);
          setLegacyPdfList([]);
          setNewPdfGroups([]);
          setFreePdfFiles(new Set());
        }
      }
    };

    fetchPdfData();
  }, [activeTab, baseLanguage, forceLegacyPdf, isCyrillicLang, isNative, site, targetLanguage, userId, userInfo]);

  const togglePdfLevel = (level) => {
    setExpandedPdfLevels((prev) => ({
      ...prev,
      [level]: !prev[level],
    }));
  };

  const openPdf = (basePath, fileName) => {
    window.open(basePath + fileName, '_blank', 'noopener,noreferrer');
  };
	
  const handleVideoClick = () => {
    setShowModal(true);
    setActiveTab('videos');
  };  

  const renderNewPdfLayout = () => {
    if (!newPdfGroups.length) {
      return <p>{t.menu.noFiles || UI_TEXT_RU.filesNotFound}</p>;
    }

    return (
      <div className={styles.pdfModern}>
        <div className={styles.pdfModernHeader}>
          <div>
            <h3 className={styles.pdfModernTitle}>{UI_TEXT_RU.pdfTitle}</h3>
            <p className={styles.pdfModernSubtitle}>{UI_TEXT_RU.pdfSubtitle}</p>
          </div>

          {isLocked && (
            <button className={styles.buyPremiumButton} onClick={() => onGoToVip?.()}>
              {t.menu.buyPremium}
            </button>
          )}
        </div>

        <div className={styles.pdfModernLevels}>
          {newPdfGroups.map((group) => {
            const levelIsOpen = !!expandedPdfLevels[group.level];
            const freeCount = group.items.filter((item) => freePdfFiles.has(item.file)).length;

            return (
              <section key={group.level} className={styles.pdfLevelCard}>
                <div className={styles.pdfLevelTop}>
                  <button className={styles.pdfLevelToggle} onClick={() => togglePdfLevel(group.level)}>
                    <span className={styles.pdfLevelName}>{group.level}</span>
                    <span className={styles.pdfLevelCount}>
                      {group.items.length} PDF
                      {freeCount > 0 ? ` · ${freeCount} free` : ''}
                    </span>
                  </button>

                  <div className={styles.pdfDownloadAll}>
                    {isLocked ? UI_TEXT_RU.pdfFullPack : UI_TEXT_RU.pdfChooseFile}
                  </div>
                </div>

                {levelIsOpen && (
                  <div className={styles.pdfTopicList}>
                    {group.items.map((item) => {
                      const isFree = freePdfFiles.has(item.file);
                      const canOpen = isFree || !isLocked;

                      return (
                        <div key={item.file} className={styles.pdfTopicRow}>
                          <div className={styles.pdfTopicTitleWrap}>
                            <span className={styles.pdfTopicTitle}>{item.topic}</span>
                            {isFree && <span className={styles.pdfBadgeFree}>free</span>}
                          </div>

                          <button
                            className={`${styles.pdfTopicDownload} ${!canOpen ? styles.lockedPdf : ''}`}
                            disabled={!canOpen}
                            onClick={() => canOpen && openPdf(newPdfPath, item.file)}
                          >
                            {UI_TEXT_RU.download}
                            {!canOpen && <span className={styles.lockIcon}>🔒</span>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLegacyPdfLayout = () => (
    <div className={styles.pdfContainer}>
      {legacyPdfList.length > 0 ? (
        <>
          <div className={styles.pdfRow}>
            {legacyPdfList.slice(0, 4).map((pdf) => (
              <button
                key={pdf.name}
                className={styles.pdfButton}
                onClick={() => openPdf(legacyPdfPath, pdf.file)}
              >
                {pdf.name}
              </button>
            ))}
          </div>

          {isLocked && (
            <div className={styles.lockedSection}>
              <p className={styles.lockedText}>{t.menu.levelLocked}</p>
              <button className={styles.buyPremiumButton} onClick={() => onGoToVip?.()}>
                {t.menu.buyPremium}
              </button>
            </div>
          )}

          <div className={styles.pdfRow}>
            {legacyPdfList.slice(4).map((pdf) => (
              <button
                key={pdf.name}
                className={`${styles.pdfButton} ${isLocked ? styles.lockedPdf : ''}`}
                disabled={isLocked}
                onClick={() => !isLocked && openPdf(legacyPdfPath, pdf.file)}
              >
                {pdf.name}
                {isLocked && <span className={styles.lockIcon}>🔒</span>}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p>{t.menu.noFiles || UI_TEXT_RU.filesNotFound}</p>
      )}
    </div>
  );

  return (
    <>
      {isCyrillicLang && phrasesCount < 1000 && (
        <div className={styles.youtubeHintWrapper}>
          <button className={`${styles.circleButton} ${styles.youtubeButton}`} onClick={handleVideoClick}>
            <img src={imgVideo} alt="Видео" className={styles.icon} />
          </button>
        </div>
      )}

      <div className={styles.buttonContainer}>
        <button className={`${styles.circleButton} ${styles.userButton}`} onClick={() => {
          //console.log('[Logo] User clicked profile button - opening modal');
          setShowModal(true);
        }}>
          <img src={getAvatarSrc(userInfo?.avatar, isNative, site)} alt="User" className={styles.userIcon} />
        </button>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          //console.log('[Logo] Modal close triggered');
          setShowModal(false);
        }}
        tabs={modalTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {activeTab === 'videos' && isCyrillicLang && (
          <div className={styles.videoShowcase}>
            {videoLinks.length > 0 ? (
              <div className={styles.videoBlock}>
                <div className={styles.videoIntro}>{buildVideoPromoText(videoLinks.length)}</div>
                <div className={styles.videoAccordion}>
                  {videoLinks.map((video, index) => {
                    const isOpen = index === activeVideoIndex;
                    return (
                      <div key={`${video.url}-${index}`} className={styles.videoAccordionItem}>
                        <button
                          className={`${styles.videoAccordionButton} ${
                            isOpen ? styles.videoAccordionButtonActive : ''
                          }`}
                          onClick={() => setActiveVideoIndex((prev) => (prev === index ? null : index))}
                        >
                          <span className={styles.videoAccordionTitle}>{video.title}</span>
                          <span className={styles.videoAccordionChevron} aria-hidden="true">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>
                        {isOpen && (
                          <div className={styles.videoAccordionPanel}>
                            <InlineVideo url={video.url} title={video.title} showTitle={false} />
                            {video.text && <p className={styles.videoDescription}>{video.text}</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p>{UI_TEXT_RU.videoNotFound}</p>
            )}
          </div>
        )}

        {activeTab === 'pdf' && isCyrillicLang && (
          <>
            {useLegacyPdfLayout ? renderLegacyPdfLayout() : renderNewPdfLayout()}
          </>
        )}

        {activeTab === 'profile' && (
          <div className={styles.profileTab}>
            <div className={styles.profileCard}>
              <img
                src={getAvatarSrc(userInfo?.avatar, isNative, site)}
                alt="Profile"
                className={styles.profileAvatar}
              />
              <div>
                <div className={styles.profileName}>{userInfo?.name || brandName}</div>
                <div className={styles.profileMeta}>
                  {phrasesCount.toLocaleString()} {UI_TEXT_RU.phrasesInPractice}
                </div>
              </div>
            </div>

            <div className={styles.profileActions}>
              <button
                className={styles.primaryAction}
                onClick={() => {
                  setShowModal(false);
                  onOpenProfile?.();
                }}
              >
                {UI_TEXT_RU.profileOpen}
              </button>
              <button
                className={styles.secondaryAction}
                onClick={() => {
                  setShowModal(false);
                  onOpenLanguageSettings?.();
                }}
              >
                {UI_TEXT_RU.profileLanguageLevel}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Logo;
